'use client';
import {
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';

import { formatDate } from '@/lib/DateHelpers';

export type CardProps = {
  slug: string;
  title: string;
  paragraph?: string;
  imageUrl?: string;
  authorName?: string;
  authorAvatar?: string;
  publishedAt?: string;
};

export function PageCard({
  slug,
  title,
  paragraph,
  imageUrl,
  authorName,
  authorAvatar,
  publishedAt,
}: CardProps) {
  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        flex: 1,
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0,
        background: '#fff',
        border: `1px solid ${theme.palette.divider}`,
        width: '100%',
        boxShadow: 'none !important',
        height: '100%',
        p: 0,
      })}
    >
      <CardActionArea
        component={Link}
        href={`/${slug}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ flexShrink: 0 }}
      >
        <Box
          sx={{
            height: 180,
            width: '100%',
            overflow: 'hidden',
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
          }}
        >
          <Box
            component="img"
            src={imageUrl || '/assets/placeholder-thumbnail.png'}
            alt={title}
            loading="lazy"
            sx={{
              width: '100%',
              height: 'auto',
              objectFit: 'cover',
              transform: 'translateY(-10%)',
            }}
          />
        </Box>
      </CardActionArea>

      <CardContent
        component={Link}
        href={`/${slug}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          px: 3,
          py: 2.5,
          textDecoration: 'none',
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          mb={1.5}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            '&:hover': {
              textDecoration: 'none',
            },
          }}
        >
          {title}
        </Typography>

        {paragraph && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={(theme) => ({
              flexGrow: 1,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textAlign: 'left',
              fontSize: theme.typography.medium.fontSize,
            })}
          >
            {paragraph}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" spacing={1} mt="auto" pt={2}>
          {authorAvatar && (
            <Avatar
              src={authorAvatar}
              alt={authorName || 'Author'}
              sx={{ width: 28, height: 28 }}
            />
          )}

          {authorName && (
            <Link
              href="/about"
              underline="hover"
              fontWeight={600}
              sx={(theme) => ({
                fontSize: theme.typography.small.fontSize,
                color: '#4e44ce',
              })}
            >
              {authorName}
            </Link>
          )}

          {publishedAt && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={(theme) => ({
                ml: 'auto',
                fontSize: theme.typography.small.fontSize,
              })}
            >
              {formatDate(publishedAt)}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
